import { useState, useContext, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { AuthContext } from "@auth";
import { useGetGroupLifestyleAnalytics, useGetLifestyleGroup } from "@queries";
import { LSAGroup, LSAGroupUpdateInput, LSACategoryInput, LSACategory } from "../../../../types";
import { toast } from "sonner";
import { useUpdateLifestyleGroup, useDeleteLifestyleGroup} from "../../../../graphql/mutation-hooks";
import { useTranslation } from "react-i18next";

// Placeholder mutation hooks - replace with actual implementations when backend is ready
// const useUpdateLifestyleGroup = () => {
//   return async (groupId: string, groupUpdate: LSAGroupUpdateInput) => {
//     console.log('Update group:', groupId, groupUpdate);
//     // TODO: Implement actual mutation
//   };
// };

// const useDeleteLifestyleGroup = () => {
//   return async (groupId: string) => {
//     console.log('Delete group:', groupId);
//     // TODO: Implement actual mutation
//   };
// };

export interface LifestyleManageGroupPageTemplateProps {
  groupDetails: LSAGroup | null;
  isLoading: boolean;
  hasLoaded: boolean;
  error: string | null;
  formData: LSAGroupUpdateInput;
  setFormData: (data: LSAGroupUpdateInput) => void;
  isDirty: boolean;
  onSave: (dataOverride?: LSAGroupUpdateInput) => Promise<void>;
  onDelete: () => void;
  onCancel: () => void;
  validationErrors: Record<string, string>;
  isSaving: boolean;
  isDeleting: boolean;
}

export const useLifestyleManageGroupPage = (): LifestyleManageGroupPageTemplateProps => {
  const navigate = useNavigate();
  const location = useLocation();
  const { partner, lifestyleGroup: contextLifestyleGroup } = useContext(AuthContext);
  const { t } = useTranslation();
  
  // Get group ID from URL params or state
  const groupId = new URLSearchParams(location.search).get('groupId') || 
                  location.state?.groupId || 
                  partner?.lsaGroups?.[0]?.groupId;

  const {
    getLifestyleGroup,
    lifestyleGroup: fetchedLifestyleGroup,
    loading,
    error,
  } = useGetLifestyleGroup();
  const updateLifestyleGroup = useUpdateLifestyleGroup();
  const deleteLifestyleGroup = useDeleteLifestyleGroup();

  const [formData, setFormData] = useState<LSAGroupUpdateInput>({});
  const [isDirty, setIsDirty] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [groupDetails, setGroupDetails] = useState<LSAGroup | null>(null);
  const [hasRequestedGroup, setHasRequestedGroup] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);

  const lifestyleGroupData = fetchedLifestyleGroup ?? contextLifestyleGroup;

  // Load group details on mount
  useEffect(() => {
    if (groupId) {
      console.log("fetching lifestyle group with id: ", groupId);
      setHasRequestedGroup(true);
      getLifestyleGroup({ variables: { groupId } });
    }
  }, [groupId, getLifestyleGroup]);

  // Initialize form data with mock data
  useEffect(() => {
    if (groupId && lifestyleGroupData) {
      // const selectedGroup = partner.lsaGroups?.find(g => g.groupId === groupId);
      console.log("lifestyleGroup: ", lifestyleGroupData);
      const details: LSAGroup = lifestyleGroupData;
      setGroupDetails(details);
      const categories: LSACategoryInput[] = details.categories.map((category: LSACategory) => ({
        id: category.id,
        name: category.name,
        description: category.description,
        examples: category.examples,
        icon: category.icon,
      }));
      setFormData({
        name: details.name,
        yearlySpendingLimit: details.yearlySpendingLimit,
        currency: details.currency,
        prorated: details.prorated,
        startDate: details.startDate?.split('T')[0],
        resetDate: details.resetDate?.split('T')[0],
        runOffPeriod: details.runOffPeriod,
        coveragePercentage: details.coveragePercentage,
        categories: categories,
      });
    }
  }, [groupId, lifestyleGroupData]);

  useEffect(() => {
    if (lifestyleGroupData || error || (!groupId && !hasRequestedGroup)) {
      setHasLoaded(true);
    }
  }, [lifestyleGroupData, error, groupId, hasRequestedGroup]);

  const isPageLoading = loading || (!hasLoaded && hasRequestedGroup);

  // Form validation
  const validateForm = (data: LSAGroupUpdateInput) => {
    const errors: Record<string, string> = {};

    if (!data.name || data.name.trim().length === 0) {
      errors.name = t("lifestyleManageGroup.form.validation.groupNameRequired");
    } else if (data.name.trim().length < 3) {
      errors.name = t("lifestyleManageGroup.form.validation.groupNameMinLength");
    }

    if (!data.yearlySpendingLimit || data.yearlySpendingLimit <= 0) {
      errors.yearlySpendingLimit = t("lifestyleManageGroup.form.validation.yearlySpendingLimit");
    }

    if (!data.coveragePercentage || data.coveragePercentage < 0 || data.coveragePercentage > 100) {
      errors.coveragePercentage = t("lifestyleManageGroup.form.validation.coveragePercentage");
    }

    if (data.runOffPeriod && (data.runOffPeriod < 0 || data.runOffPeriod > 365)) {
      errors.runOffPeriod = t("lifestyleManageGroup.form.validation.runOffPeriod");
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Track form changes
  const handleFormDataChange = (newData: LSAGroupUpdateInput) => {
    console.log("newData: ", newData);
    setFormData(newData);
    setIsDirty(true);
    validateForm(newData);
  };

  // Save changes
  const onSave = async (dataOverride?: LSAGroupUpdateInput) => {
    if (!groupId) {
      toast.error(t("lifestyleManageGroup.form.validation.noGroupSelected"));
      return;
    }

    const dataToSave = dataOverride ?? formData;

    if (!validateForm(dataToSave)) {
      toast.error(t("lifestyleManageGroup.form.validation.fixValidation"));
      return;
    }

    setIsSaving(true);
    try {
      console.log("formData: ", dataToSave);
      const categories: LSACategoryInput[] = (dataToSave.categories || []).map((category: LSACategory) => ({
        id: category.id,
        name: category.name,
        description: category.description,
        examples: category.examples,
        icon: category.icon,
      }));
      const payload: LSAGroupUpdateInput = {
        ...dataToSave,
        categories,
      };
      await updateLifestyleGroup(groupId, payload);
      setIsDirty(false);
      toast.success(t("lifestyleManageGroup.toasts.updateSuccess"));
      // navigate("/dashboard/lifestyle/main");
    } catch (error) {
      console.error("Failed to update group:", error);
      toast.error(t("lifestyleManageGroup.toasts.updateFailed"));
    } finally {
      setIsSaving(false);
    }
  };

  // Delete group
  const onDelete = async () => {
    if (!groupId) {
      toast.error(t("lifestyleManageGroup.form.validation.noGroupSelected"));
      return;
    }
    setIsDeleting(true);
    console.log("deleting group with id: ", groupId);
    try {
      await deleteLifestyleGroup(groupId);
      toast.success(t("lifestyleManageGroup.toasts.deleteSuccess"));
      setIsDeleting(false);
      navigate("/dashboard/lifestyle/main");
    } catch (error) {
      console.error("Failed to delete group:", error);
      toast.error(t("lifestyleManageGroup.toasts.deleteFailed"));
    } finally {
      setIsDeleting(false);
    }
  };

  // Cancel and go back
  const onCancel = () => {
    // Not sure if we require a cancel confirmation, the page doesn't have much data on it
    
    // if (isDirty) {
    //   const confirmed = window.confirm(
    //     "You have unsaved changes. Are you sure you want to leave without saving?"
    //   );
    //   if (!confirmed) return;
    // }
    navigate("/dashboard/lifestyle/main");
  };

  return {
    groupDetails,
    isLoading: isPageLoading,
    hasLoaded,
    error: error ? error.message : null,
    formData,
    setFormData: handleFormDataChange,
    isDirty,
    onSave,
    onDelete,
    onCancel,
    validationErrors,
    isSaving,
    isDeleting,
  };
};
